import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import Modal, { ModalBody, ModalHeader, Sheets } from '@ingka/modal';
import { selectBuCode, selectOrderNoSource } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectPaymentDetails } from '@repo/api-adapters/selectors/payment.selectors';
import { useStoreDetails } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { getCountryLanguage, isUtcDate, resolveTimezone } from '@repo/utils';

import { AssetPaymentEditFooter } from '@/components/modals/asset-payment-modal/asset-payment-edit-footer.component.tsx';
import { AssetPaymentEdit } from '@/components/modals/asset-payment-modal/asset-payment-edit.component.tsx';
import { AssetPaymentView } from '@/components/modals/asset-payment-modal/asset-payment-view.component.tsx';

import { getServiceEndpoint } from '@/api/helpers.api.ts';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { MANUAL_PAYMENTS_EVENTS } from '@/utils/manual-payments.utils.ts';
import { getAppConfig } from '@/utils/window.utils.ts';

import { useAssetPaymentEditable, useManualPaymentsEventHandlers } from './asset-payment-modal.hooks';

interface Props {
    visible: boolean;
    onClose: () => void;
}

export type Step = 'VIEW' | 'EDIT';

export const AssetPaymentModal = ({ visible, onClose }: Props) => {
    const { assetId, assetType } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const [step, setStep] = useState<Step>('VIEW');
    const assetBuCode = selectBuCode(data) ?? '';
    const config = getAppConfig();
    const storeDetailsBaseUrl = getServiceEndpoint(config.api.services.classicSalja, 'storeDetails');
    const { data: storeDetails } = useStoreDetails(storeDetailsBaseUrl, assetBuCode);
    const storeTimezone = storeDetails?.TimeZone ?? 'UTC';
    const resolvedTimezone = resolveTimezone(storeTimezone);
    const orderNoSourceResult = selectOrderNoSource(data);
    const { country } = getCountryLanguage();
    const orderNoSource =
        typeof orderNoSourceResult === 'function' ? orderNoSourceResult(country) : orderNoSourceResult;
    const { paymentDueDate } = selectPaymentDetails(data);
    const paymentDueDateUtc = isUtcDate(paymentDueDate)
        ? dayjs.utc(paymentDueDate).format('YYYY-MM-DD[T]HH:mm:ss[Z]')
        : dayjs(paymentDueDate).tz(resolvedTimezone).utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]');
    const [selectedPaymentDate, setSelectedPaymentDate] = useState<string>(paymentDueDateUtc);

    const { isEditable: isEditPaymentDateEnabled, daysBetween } = useAssetPaymentEditable();
    const { handleNavigateBack, handlePaymentChanged } = useManualPaymentsEventHandlers();

    useEffect(() => {
        globalThis.addEventListener(MANUAL_PAYMENTS_EVENTS.navigateBack, handleNavigateBack);
        globalThis.addEventListener(MANUAL_PAYMENTS_EVENTS.paymentFailure, handlePaymentChanged);
        globalThis.addEventListener(MANUAL_PAYMENTS_EVENTS.paymentSuccess, handlePaymentChanged);

        return () => {
            globalThis.removeEventListener(MANUAL_PAYMENTS_EVENTS.navigateBack, handleNavigateBack);
            globalThis.removeEventListener(MANUAL_PAYMENTS_EVENTS.paymentFailure, handlePaymentChanged);
            globalThis.removeEventListener(MANUAL_PAYMENTS_EVENTS.paymentSuccess, handlePaymentChanged);
        };
    }, [handleNavigateBack, handlePaymentChanged]);

    const isSaveButtonDisabled = !paymentDueDate || daysBetween < 0 || paymentDueDateUtc === selectedPaymentDate;

    const getView = () => {
        switch (step) {
            case 'VIEW': {
                return (
                    <AssetPaymentView
                        setStep={setStep}
                        isEditEnabled={isEditPaymentDateEnabled}
                        closeModal={handleClose}
                    />
                );
            }
            case 'EDIT': {
                return <AssetPaymentEdit setSelectedPaymentDate={setSelectedPaymentDate} />;
            }
            default: {
                return null;
            }
        }
    };

    const getFooter = () => {
        switch (step) {
            case 'EDIT': {
                return (
                    <AssetPaymentEditFooter
                        assetId={assetId}
                        orderNoSource={orderNoSource ?? ''}
                        paymentDate={selectedPaymentDate}
                        onClose={handleClose}
                        isButtonDisabled={isSaveButtonDisabled}
                    />
                );
            }
            default: {
                return null;
            }
        }
    };

    const handleClose = () => {
        setStep('VIEW');
        setSelectedPaymentDate(paymentDueDateUtc);
        onClose();
    };

    const handleBack = () => {
        setStep('VIEW');
        setSelectedPaymentDate(paymentDueDateUtc);
    };

    return (
        <Modal data-testid="asset-payment-modal" visible={visible} handleCloseBtn={handleClose}>
            <Sheets
                size="small"
                aria-label={translate('asset_payment_modal_header')}
                header={
                    <ModalHeader
                        data-testid="asset_payment_modal_header"
                        title={translate('asset_payment_modal_header')}
                        {...(step === 'EDIT' ? { backBtnClick: handleBack } : {})}
                    />
                }
                footer={getFooter()}
            >
                <ModalBody>{getView()}</ModalBody>
            </Sheets>
        </Modal>
    );
};
